'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { Input } from '@repo/ui/components/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@repo/ui/components/input-otp';
import { useAuth } from '@repo/ui/providers/auth-context';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

interface SignInDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const otpSchema = z.object({
  otp: z.string().length(6, 'Verification code must be 6 characters'),
});

type EmailFormData = z.infer<typeof emailSchema>;
type OtpFormData = z.infer<typeof otpSchema>;

export function SignInDialog({ isOpen, onClose }: SignInDialogProps) {
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const t = useTranslations('auth');

  const { signIn, verifyOTP } = useAuth();

  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const otpForm = useForm<OtpFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: '',
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setShowOtpInput(false);
    }
    if (!isOpen || !showOtpInput) {
      setUserEmail('');
      emailForm.reset();
      otpForm.reset();
    }
  }, [isOpen, showOtpInput]);

  const onEmailSubmit = async (data: EmailFormData) => {
    setIsLoading(true);
    try {
      await signIn({ email: data.email });
      setUserEmail(data.email);
      setShowOtpInput(true);
      otpForm.reset();
    } catch (error) {
      toast.error('Failed to send verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onOtpSubmit = async (data: OtpFormData) => {
    setIsLoading(true);
    try {
      await verifyOTP({ email: userEmail, token: data.otp });
      toast.success('Successfully signed in');
      onClose();
    } catch (error) {
      otpForm.setError('otp', {
        type: 'manual',
        message: 'Invalid verification code. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='text-center text-xl font-bold'>
            {t('sign_in')}
          </DialogTitle>
          <DialogDescription className='text-center'>
            {showOtpInput ? t('enter_code') : t('enter_email')}
          </DialogDescription>
        </DialogHeader>
        {!showOtpInput ? (
          <form
            onSubmit={emailForm.handleSubmit(onEmailSubmit)}
            className='space-y-6'
          >
            <div className='py-2 w-full'>
              <div className='space-y-2'>
                <label
                  htmlFor='email'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                >
                  {t('email')}
                </label>
                <Input
                  id='email'
                  type='email'
                  {...emailForm.register('email')}
                  placeholder={t('email_placeholder')}
                  className={`w-full ${emailForm.formState.errors.email ? 'border-red-500' : ''}`}
                  autoComplete='email'
                />
                {emailForm.formState.errors.email && (
                  <p className='text-sm text-red-500' role='alert'>
                    {emailForm.formState.errors.email.message}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type='submit' disabled={isLoading} className='w-full'>
                {isLoading ? t('processing') : t('send_code')}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form
            onSubmit={otpForm.handleSubmit(onOtpSubmit)}
            className='space-y-6'
          >
            <div className='py-2 w-full'>
              <div className='space-y-4'>
                <label
                  htmlFor='otp'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                >
                  {t('verification_code')}
                </label>
                <div className='w-full'>
                  <InputOTP
                    maxLength={6}
                    value={otpForm.watch('otp')}
                    onChange={(value) => {
                      otpForm.setValue('otp', value);
                      if (value.length === 6) {
                        otpForm.handleSubmit(onOtpSubmit)();
                      }
                    }}
                    className='w-full gap-2'
                  >
                    <InputOTPGroup className='w-full justify-between '>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <InputOTPSlot
                          key={index}
                          index={index}
                          className='flex-1 h-12 text-2xl'
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {otpForm.formState.errors.otp && (
                  <p className='text-sm text-red-500 text-center' role='alert'>
                    {otpForm.formState.errors.otp.message}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type='submit' disabled={isLoading} className='w-full'>
                {isLoading ? t('processing') : t('verify_code')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
